import { cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo } from "react"

import { Button } from "@/components/Button/Button.tsx"
import {
  DropdownMenu,
  DropdownMenuItemLike,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { DropdownWithFilter } from "@/components/DropdownMenu/DropdownWithFilter.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { HighlightMatches } from "@/components/HighlightMatches/HighlightMatches.tsx"
import { AddAttachment } from "@/components/PromptInput/AddAttachment.tsx"
import { AddContextModel } from "@/components/PromptInput/AddContextModel.ts"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useBatchLoader } from "@/hooks/useBatchLoader.ts"
import { useFindFiles } from "@/hooks/useFindFiles.ts"
import { useListFiles } from "@/hooks/useListFiles.ts"
import { useModel } from "@/hooks/useModel.ts"
import {
  CheckmarkMedium,
  ChevronRightLight,
  CreateRegular,
  FilePlainLight,
  FolderLight,
  Icon20
} from "@/icons/Icons.ts"
import { getSortedFiles } from "@/lib/displayUtils.ts"
import { useCurrentProject } from "@/routes/projects/projectHooks.ts"
import { Rotate0, Rotate180, Rotate45 } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type { PromptInputModel } from "@/components/PromptInput/PromptInputModel.ts"


export const AddContext = observer((props: {
  model: PromptInputModel
}) => {
  const contextModel = useModel(() => new AddContextModel(props.model), [props.model])

  const open = contextModel.open.get()

  return (
    <DropdownMenu open={open} onOpenChange={contextModel.setOpen}>
      <Tooltip content={open ? undefined : "Add files and images"}>
        <DropdownMenuTrigger data-testid="add-files-and-images-button">
          <PromptInputStyles.FooterIconContainer>
            <Vertical className={open ? Rotate45 : Rotate0}>
              <CreateRegular className={Icon20} />
            </Vertical>
          </PromptInputStyles.FooterIconContainer>
        </DropdownMenuTrigger>
      </Tooltip>
      {open && <AddContextDropdown model={props.model} contextModel={contextModel} />}
    </DropdownMenu>
  )
})

export const AddContextDropdown = observer((props: {
  model: PromptInputModel
  contextModel: AddContextModel
}) => {
  const project = useCurrentProject()
  const projectRepository = project.repository.name

  const foundItems = useBatchLoader(
    useFindFiles,
    (batch) => {
      return Array.from(new Map(batch.data.map((file) => [file.path, file])).values())
    },
    project.key,
    projectRepository,
    null,
    props.contextModel.inputValue.get()
  )

  const listedItems = useListFiles(project.key, projectRepository, null, props.contextModel.listedPath.get(), null, ["directories"], 100)

  const sortedListedItems = useMemo(() => getSortedFiles(listedItems.data), [listedItems.data])

  const nonRootPathSelected = props.contextModel.listedPath.get() !== null && props.contextModel.listedPath.get() !== ""

  useEffect(() => {
    return () => {
      if (!props.contextModel.open.get()) {
        runInAction(() => props.contextModel.inputValue.set(""))
        foundItems.reset()
      }
    }
  })

  return (
    <DropdownWithFilter
      items={foundItems}
      inputValue={props.contextModel.inputValue.get()}
      setInputValue={props.contextModel.setInputValue}
      beforeInput={nonRootPathSelected ? (
        <Button kind="icon" onClick={props.contextModel.navigateUp}>
          <ChevronRightLight className={cx(Icon20, Rotate180)} />
        </Button>
      ) : null}
      header={nonRootPathSelected ? (
        <PathHeader>
          <EllipsisContainer><Ellipsis>{props.contextModel.listedPath.get()}</Ellipsis></EllipsisContainer>
        </PathHeader>
      ) : null}
      renderItem={(file, index) => {
        const name = file.path.substring(file.path.lastIndexOf("/") + 1)
        const path = file.path.substring(0, file.path.lastIndexOf("/"))
        return (
          <DropdownMenuItemLike key={index} onClick={() => props.contextModel.onSelect(file)}>
            <Vertical>
              <Horizontal alignItems="center" gap="4px">
                <Tooltip content={name}>
                  <EllipsisContainer>
                    <Ellipsis>
                      <HighlightMatches text={name} query={props.contextModel.inputValue.get()} />
                    </Ellipsis>
                  </EllipsisContainer>
                </Tooltip>
                <Spring />
                {props.contextModel.isFileInContext(file)
                  ? (
                    <Tooltip content="Part of context">
                      <CheckmarkMedium className={Icon20} color="var(--success-color)" />
                    </Tooltip>
                  )
                  : null
                }
              </Horizontal>

              <Tooltip content={path}>
                <EllipsisContainer>
                  <Path>
                    <HighlightMatches text={path} query={props.contextModel.inputValue.get()} />
                  </Path>
                </EllipsisContainer>
              </Tooltip>
            </Vertical>
          </DropdownMenuItemLike>
        )
      }}
      noItemsFound={(value) => <>No files found matching <strong>{value}</strong></>}
      placeholder={
        <>
          {!listedItems.isLoading && (!sortedListedItems || sortedListedItems.length === 0)
            ? <SecondaryTextSpan>The repository is empty</SecondaryTextSpan>
            : sortedListedItems?.map((file) => {
              const name = file.path.substring(file.path.lastIndexOf("/") + 1)
              return (
                <DropdownMenuItemLike key={file.path} onClick={() => {
                  if (file.type === "DIR") {
                    props.contextModel.onOpen(file)
                  } else {
                    props.contextModel.onSelect(file)
                  }
                }}>
                  <FileBrowserDropdownItem>
                    {file.type === "DIR" ? <FolderLight className={Icon20} /> : <FilePlainLight className={Icon20} />}
                    <EllipsisContainer>
                      <Tooltip content={name}>
                        {file.type === "DIR" ? <Directory>{name}</Directory> : <Ellipsis>{name}</Ellipsis>}
                      </Tooltip>
                    </EllipsisContainer>
                    <Spring />
                    {props.contextModel.isFileInContext(file)
                      ? (
                        <Tooltip content="Part of context">
                          <CheckmarkMedium className={Icon20} color="var(--success-color)" />
                        </Tooltip>
                      )
                      : (file.type === "DIR"
                          ? (
                            <Tooltip content="Add directory to context">
                              <Button
                                kind="alternative"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  props.contextModel.onSelect(file)
                                }}
                              >Add</Button>
                            </Tooltip>
                          )
                          : null
                      )
                    }
                  </FileBrowserDropdownItem>
                </DropdownMenuItemLike>
              )
            })}
        </>
      }
      footer={props.contextModel.inputValue.get() === "" && !nonRootPathSelected ? (
        <>
          <DropdownMenuSeparator />
          <FileBrowserFooter>
            <AddAttachment model={props.model} setOpen={props.contextModel.setOpen} />
          </FileBrowserFooter>
        </>
      ) : null}
    />
  )
})

const FileBrowserDropdownItem = styled(Horizontal)`
  align-items: center;
  gap: 4px;

  min-height: 28px;
`

const FileBrowserFooter = styled.div`
  padding-top: 12px;

  color: var(--secondary-text-color);

  ${textStyles.smallerFontSize};
`

const Directory = styled(Ellipsis)`
  font-weight: 600;
`

const Path = styled(Ellipsis)`
  color: var(--secondary-text-color);
`

const PathHeader = styled.div`
  padding-left: 2px;
  color: var(--secondary-text-color);
  ${textStyles.smallerFontSize};
`
